
import { mapGetters } from "vuex";
import { svgCode } from "@/utils/svg-code.js";
import { getUserInfoAPI, getUserKeyAPI } from "@/api";
import { ResponseCode } from "@/constants";
import { sendAli } from "@/utils/send-ali.js";
import "@/assets/css/index.css";
import { eventBus } from "~/utils/eventBus.js";
import Footer from "~/components/footer.vue";

export default {
  components: { Footer },
  data() {
    return {
      isMobile: false,
      logo: true, // 如果有logo则设为true
      currentPath: '/ai-tattoo-generator/',
      isLoading: true, // 默认显示加载状态
      isGenerating: false, // 标记是否正在执行生成操作
      navigating: false, // 是否正在进行页面导航
      svgCode: svgCode,
      aLiId: "web-ai-tattoo", // Specific aLiId for layout context
      logstore: "web-remaker",
      config: { credits: 0, day_sign_credits: 0, isLogin: false }, // Minimal config for header rendering before page load
      emailDialogVisible: false,
      viewApi: 0,
      _configPlaceholder: {}, // Renamed to avoid conflict if page passes 'config' prop
      resizeTimeout: null,
      footerObserver: null,

      // 多语言选择相关数据
      availableLanguages: [
        { code: "en", name: "English" },
        { code: "zh", name: "中文" },
        { code: "jp", name: "日本語" },
        { code: "kr", name: "한국어" },
        { code: "de", name: "Deutsch" },
        { code: "pt", name: "Português" },
        { code: "es", name: "Español" },
        { code: "fr", name: "Français" }
      ],
    }
  },
  computed: {
    ...mapGetters(["getUserInfo", "lan"]),
    currentLanguageName() {
      const lang = this.availableLanguages.find(item => item.code === this.$i18n.locale);
      return lang ? lang.name : '';
    }
  },
  mounted() {
    sendAli(this.aLiId, "open_home");
    window.addEventListener('resize', this.onResize);

    this.$root.$on('content-loaded', this.hideLoading);
    this.$root.$on('content-loading', this.showLoading);
    this.$root.$on('generate-start', this.startGenerating);
    this.$root.$on('generate-end', this.endGenerating);

    eventBus.$on('loginEmitBus', this.reachedLoginEmit);
    eventBus.$on('priceEmitBus', this.reachedPriceEmit);

    // 在创建组件时预先检测是否为移动设备
    this.checkMobilePreload();
    if (this.$route) {
      this.currentPath = this.$route.path;
    } else {
      this.currentPath = '/';
    }
    // 初始化多语言
    this.initCurrentLanguage();

    if (this.getUserInfo) {
      this.config = { ...this.config, ...this.getUserInfo };
    }
    this.getUserEmit();

    this.isLoading = true; // Explicitly set loading true before fetch
    if (this.getUserInfo) {
      this.config = { ...this.config, ...this.getUserInfo };
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    this.$root.$off('content-loaded', this.hideLoading);
    this.$root.$off('content-loading', this.showLoading);
    this.$root.$off('generate-start', this.startGenerating);
    this.$root.$off('generate-end', this.endGenerating);

    eventBus.$off('loginEmitBus', this.reachedLoginEmit);
    eventBus.$off('priceEmitBus', this.reachedPriceEmit);

    // 确保移除所有的导航状态
    document.body.classList.remove('page-navigating');

    // 移除 MutationObserver
    if (this.footerObserver) {
      this.footerObserver.disconnect();
    }
  },
  methods: {
    checkMobilePreload() {
      // 预先检测是否为移动设备，避免布局切换闪烁
      if (process.client) {
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes("mobile")) {
          this.isMobile = true;
        } else {
          this.isMobile = window.innerWidth < 1050;
        }
      } else {
        // 服务端渲染时，默认为非移动设备
        this.isMobile = false;
      }
    },
    onResize() {
      // 使用节流函数避免频繁触发
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        try {
          // 只在非移动设备和非移动用户代理的情况下检查窗口大小
          const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';
          if (userAgent && !userAgent.includes("mobile")) {
            this.isMobile = window.innerWidth < 1050;
          } else if (userAgent && userAgent.includes("mobile")) {
            this.isMobile = true; // Already mobile
          } else {
            // Fallback for server-side or environments without navigator
            this.isMobile = typeof window !== 'undefined' ? window.innerWidth < 1050 : false;
          }

          // 延迟触发以允许DOM更新
          this.$nextTick(() => {
            // 通知其他组件响应式布局变化完成
            this.$root.$emit('layout-resize-complete', this.isMobile);
          });
        } catch (err) {
          console.error('Error in resize handler:', err);
        }
      }, 150); // 150ms的延迟以防止频繁触发
    },
    navigateTo(path) {
      // 如果已经在当前路径，不执行任何操作
      if (this.currentPath === path) {
        return;
      }

      // 设置加载状态
      this.isLoading = true;

      // 确保导航栏保持可见
      document.body.classList.add('page-navigating');

      try {
        // 直接重置滚动位置
        if (this.isMobile) {
          window.scrollTo(0, 0);
        } else {
          const contentArea = document.querySelector('.content-area');
          if (contentArea) {
            contentArea.scrollTop = 0;
          }
        }

        // 进行路由导航，使用try-catch处理而不依赖Promise
        const navResult = this.$router.push(path);

        // 检查返回值是否为Promise
        if (navResult && typeof navResult.then === 'function') {
          navResult
            .then(() => {
              this.currentPath = path;
            })
            .catch(err => {
              // 只记录不是导航取消的错误
              if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err);
              }
            });
        } else {
          // 如果不是Promise，直接更新当前路径
          this.currentPath = path;
        }
      } catch (err) {
        console.error('Navigation error:', err);
      }

      // 不管成功或失败，总是在适当的时间后重置状态
      setTimeout(() => {
        document.body.classList.remove('page-navigating');
      }, 300);
    },
    hideLoading() {
      this.isLoading = false;
    },
    showLoading() {
      if (!this.isGenerating) { // 只有在非生成状态下才显示全局加载
        this.isLoading = true;
      }
    },
    startGenerating() {
      this.isGenerating = true;
    },
    endGenerating() {
      this.isGenerating = false;
    },

    // Methods for extracted header
    jump(url) {
      window.open(url);
    },
    loginEmit() {
      if (this.$refs.loginPai) {
        this.$refs.loginPai.openLoginDialog();
        sendAli(this.aLiId, "open_login_header");
      }
    },
    openUser() {
      if (this.$refs.paiUserInfo) {
        this.$refs.paiUserInfo.openUserDialog();
      }
    },
    priceEmit() {
      if (this.$refs.pricePai) {
        sendAli(this.aLiId, "click_price_header");
        this.$refs.pricePai.openPriceDialog();
      }
    },
    emailEmit() {
      this.emailDialogVisible = true;
    },
    copy() {
      this.$message({
        message: this.$t('layout.header.copySuccess'),
        type: "success",
        showClose: true,
      });
    },
    async getUserEmit() {
      const resp = await getUserInfoAPI();
      if (resp && resp.code === ResponseCode.SUCCESS) {
        let res = JSON.parse(localStorage.getItem("userInfo")) || {};
        let obj = { ...res, ...resp.data }; // Merge, prioritizing fresh data

        obj.five_star = resp.data.five_star;
        obj.email = resp.data.email;
        obj.payment = resp.data.payment;
        obj.day_sign_new = resp.data.day_sign_new;

        let current_credit = resp.data.credits;
        let freeze_credit = resp.data.freeze_credits;
        let sign_credit = resp.data.day_sign_credits;
        let display_sign_credit = Math.max(0, sign_credit - freeze_credit);
        let display_current_credit = current_credit;
        if (sign_credit - freeze_credit < 0) {
          display_current_credit = current_credit - (freeze_credit - sign_credit);
        }
        display_current_credit = Math.max(0, display_current_credit);

        // Assuming isDay logic is true for header context or not relevant here
        obj.credits = display_current_credit;
        obj.day_sign_credits = display_sign_credit;

        // obj.ifBuyFirst = resp.data.discount_plans[this.firstCode]; // firstCode needs to be defined or handled
        localStorage.setItem("userInfo", JSON.stringify(obj));
        this.$store.commit("setUserInfo", obj);
        this.config = { ...this.config, ...obj }; // Update local config for header

        if (obj.loginType && resp.data.user_id) {
          const dataKey = {
            loginType: obj.loginType,
            userId: resp.data.user_id,
            email: resp.data.email,
          };
          const resultKey = await getUserKeyAPI(dataKey);
          if (resultKey.code === ResponseCode.SUCCESS && window.KodePay) {
            window.KodePay.register_login_information(resultKey.data.user_key);
          }
        }
      }
      return resp;
    },
    loginSuccess(val, type) {
      this.$store.commit("setUserInfo", val);
      this.getUserEmit().then(() => {
        if (this.getUserInfo) this.config = { ...this.config, ...this.getUserInfo };
      });
    },
    async payResult() {
      await this.getUserEmit();
      if (this.getUserInfo) this.config = { ...this.config, ...this.getUserInfo };
    },
    openPriceDialog() { // For pai-user-info emitting openPriceDialog
      if (this.$refs.pricePai) {
        this.$refs.pricePai.openPriceDialog();
        this.viewApi++;
      }
    },
    reachedLoginEmit() {
      if (this.$refs.loginPai) {
        this.$refs.loginPai.openLoginDialog();
      }
    },
    reachedPriceEmit() {
      this.$refs.pricePai.openPriceDialog();
    },

    // 初始化当前语言
    initCurrentLanguage() {
      // 设置 vuex 全局语言
      this.$store.commit("setLan",
        this.$i18n.locale === 'jp'
          ? 'ja'
          : this.$i18n.locale === 'kr'
            ? 'ko'
            : this.$i18n.locale
      );
    },
    changeLanguage(langCode) {
      // 修改vuex中的语言设置
      this.$store.commit('setLan',
        langCode === 'jp'
          ? 'ja'
          : langCode === 'kr'
            ? 'ko'
            : langCode
      );
      this.$i18n.setLocale(langCode);
      this.initCurrentLanguage();
    }
  },
  watch: {
    '$route.path': function (newPath) {
      this.currentPath = newPath;
      this.isLoading = true; // 路由切换时显示加载状态

      // 路由切换后也需要延时隐藏加载状态
      setTimeout(() => {
        this.isLoading = false;
      }, 300);
    },
    getUserInfo(newUserInfo) {
      // Keep header's config in sync with Vuex's getUserInfo
      if (newUserInfo) {
        this.config = { ...this.config, ...newUserInfo };
      }
    },
    // 监听语言变化，更新相关内容
    '$i18n.locale': function () {
      this.initCurrentLanguage();
    }
  }
}
